import {BehaviorSubject, delay, Observable} from "rxjs";
import {CollectionViewer, DataSource} from "@angular/cdk/collections";
import {EnergySource} from "../../../../model/energy-source";
import {EnergySourcesService} from "../../../../services/energy-source.service";

export class EnergySourcesDataSource implements DataSource<EnergySource> {
  private originalEnergySources: EnergySource[] = []

  private fetchEnergySources$: Observable<EnergySource[] | null>
  private energySourcesSubject = new BehaviorSubject<EnergySource[]>([])
  private energySources$ = this.energySourcesSubject.asObservable()

  loading$: Observable<boolean>

  constructor(private energySourcesService: EnergySourcesService) {
    this.loading$ = this.energySourcesService.loading$.pipe(delay(10))
    this.fetchEnergySources$ = this.energySourcesService.data$
  }

  connect(collectionViewer: CollectionViewer): Observable<EnergySource[]> {
    this.fetchEnergySources$.subscribe((sources) => {
      sources = sources || []

      // Deep-Copy
      this.originalEnergySources = sources.map(x => Object.assign({}, x))

      // Sprt by name
      const sorted = sources.sort((a, b) => a.name.localeCompare(b.name))
      this.energySourcesSubject.next(sorted)
    })

    return this.energySources$
  }

  disconnect(collectionViewer: CollectionViewer): void {
    this.energySourcesSubject.complete()
  }

  // --- Change Detection Logic ---

  hasCo2FactorChanged(source: EnergySource) {
    return this.original(source).co2Factor !== source.co2Factor
  }

  hasCalorificValueChanged(source: EnergySource) {
    return this.original(source).calorificValue !== source.calorificValue
  }

  private getChange(current: EnergySource) {
    const original = this.original(current)

    // Check if CO2 or calorific values has changed
    if (current.co2Factor !== original.co2Factor
      || current.calorificValue !== original.calorificValue) {
      return current
    }

    return null
  }

  getChanges() {
    const changes: EnergySource[] = []
    const currentList = this.energySourcesSubject.value

    currentList.forEach(source => {
      const change = this.getChange(source)
      if (change) {
        changes.push(change)
      }
    })

    return changes
  }

  applyUpdates(updates: EnergySource[]) {
    updates.forEach(update => {
      // Use mongo id to identify
      const original = this.originalEnergySources.find(orig => orig.mongoId === update.mongoId)!
      Object.assign(original, update)
    })
  }

  // --- Reset Logic ---

  resetCo2Factor(source: EnergySource) {
    const original = this.original(source)
    source.co2Factor = original.co2Factor
  }

  resetCalorificValue(source: EnergySource) {
    const original = this.original(source)
    source.calorificValue = original.calorificValue
  }

  private original(source: EnergySource) {
    return this.originalEnergySources.find(orig => orig.mongoId === source.mongoId)!
  }
}
